import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Plus, Save, UploadCloud, Trash2, Columns2, Square,
  Layers, Lock, ChevronUp, ChevronDown, FileJson, Sparkles, FileDown,
} from "lucide-react";
import { blueprintDesignApi } from "@/api/blueprintDesign";
import { downloadVaultAsset } from "@/api/compliance";
import CreditPaywall from "@/components/billing/CreditPaywall";

const BLOCK_TYPES = [
  { t: "heading", label: "Heading", color: "#1A1A1A" },
  { t: "text", label: "Text", color: "#64748B" },
  { t: "image", label: "Image", color: "#00B4D8" },
  { t: "button", label: "Button", color: "#D4AF37" },
  { t: "divider", label: "Divider", color: "#94A3B8" },
];
const typeMeta = (t) => BLOCK_TYPES.find((b) => b.t === t) || BLOCK_TYPES[1];
const newCell = (t = "text") => ({ type: t, label: typeMeta(t).label, color: typeMeta(t).color });

/* Phase 29 — Standalone Blueprint Design Engine. Authors a layout as a JSON
   matrix (rows → cells). Publishing is pay-to-play (Credits) and persists the
   matrix to the Vault under an asset_id. */
export default function BlueprintStudio({ onBack }) {
  const [designs, setDesigns] = useState([]);
  const [ent, setEnt] = useState(null);
  const [current, setCurrent] = useState(null); // {id, title, matrix, published, asset_id}
  const [title, setTitle] = useState("");
  const [matrix, setMatrix] = useState([]);
  const [busy, setBusy] = useState(false);
  const [paywall, setPaywall] = useState(null);
  const [rendering, setRendering] = useState(false);

  const refresh = async () => {
    try {
      const [d, e] = await Promise.all([blueprintDesignApi.list(), blueprintDesignApi.entitlement()]);
      setDesigns(d.designs || []); setEnt(e);
    } catch { /* noop */ }
  };
  useEffect(() => { refresh(); }, []);

  const loadDesign = async (id) => {
    const d = await blueprintDesignApi.get(id);
    setCurrent(d); setTitle(d.title); setMatrix(d.matrix || []);
  };
  const newDesign = () => { setCurrent(null); setTitle(""); setMatrix([]); };

  // No-Typing rule: auto-name the blueprint from its first labelled block, so the
  // user never has to invent a title. They can still add an optional nickname.
  const autoTitle = (() => {
    const firstLabel = matrix.flatMap((r) => r.cols || []).map((c) => (c.label || "").trim()).find(Boolean);
    return firstLabel ? `My ${firstLabel} Blueprint` : "My Blueprint";
  })();
  const finalTitle = title.trim() || autoTitle;

  const addRow = (cols) => setMatrix((m) => [...m, { cols: Array.from({ length: cols }, () => newCell()) }]);
  const moveRow = (i, dir) => setMatrix((m) => {
    const n = [...m]; const j = i + dir;
    if (j < 0 || j >= n.length) return n;
    [n[i], n[j]] = [n[j], n[i]]; return n;
  });
  const delRow = (i) => setMatrix((m) => m.filter((_, k) => k !== i));
  const setCell = (ri, ci, patch) => setMatrix((m) => m.map((row, k) => k !== ri ? row
    : { ...row, cols: row.cols.map((c, j) => j !== ci ? c : { ...c, ...patch }) }));

  const save = async () => {
    if (!matrix.length) { toast.error("Add at least one row"); return; }
    setBusy(true);
    try {
      if (current?.id) {
        const d = await blueprintDesignApi.update(current.id, { title: finalTitle, matrix });
        setCurrent(d);
      } else {
        const d = await blueprintDesignApi.create(finalTitle, matrix);
        setCurrent(d);
      }
      toast.success("Draft saved");
      refresh();
    } catch { toast.error("Save failed"); } finally { setBusy(false); }
  };

  const publish = async () => {
    let id = current?.id;
    setBusy(true);
    try {
      if (!id) { const d = await blueprintDesignApi.create(finalTitle, matrix); setCurrent(d); id = d.id; }
      else { await blueprintDesignApi.update(id, { title: finalTitle, matrix }); }
      const r = await blueprintDesignApi.publish(id);
      toast.success(r.charged ? `Published · ${r.cost} Credits spent` : "Published (included in plan)");
      setCurrent((c) => ({ ...(c || {}), id, published: true, asset_id: r.asset_id }));
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402 && detail) { setPaywall(detail); }
      else { toast.error(typeof detail === "string" ? detail : "Publish failed"); }
    } finally { setBusy(false); }
  };

  const renderSheet = async () => {
    if (!current?.id || !current?.published) return;
    setRendering(true);
    try {
      const r = await blueprintDesignApi.render(current.id);
      await downloadVaultAsset(r.asset_id, r.filename);
      toast.success("Layout sheet rendered & downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Render failed");
    } finally { setRendering(false); }
  };

  return (
    <div className="absolute inset-0 z-30 flex flex-col bg-slate-950" data-testid="blueprint-studio">
      <header className="shrink-0 flex items-center gap-2 border-b border-white/10 px-4 py-3">
        <button onClick={onBack} data-testid="blueprint-back" className="grid h-9 w-9 place-items-center rounded-xl border-2 border-white/15 bg-white/5 active:scale-95"><ArrowLeft size={16} className="text-white" /></button>
        <div className="leading-tight flex-1">
          <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-gold">Blueprint Engine</p>
          <h2 className="font-serif text-xl text-white flex items-center gap-1.5"><Layers size={16} /> Design Studio</h2>
        </div>
        <button onClick={newDesign} data-testid="blueprint-new" className="flex items-center gap-1 rounded-full border-2 border-gold/60 bg-white/5 px-3 py-1.5 font-mono text-[9px] font-black uppercase tracking-widest text-gold"><Plus size={12} /> New</button>
      </header>

      {/* Entitlement banner */}
      {ent && (
        <div className="shrink-0 mx-4 mt-3 rounded-xl border-2 border-gold/40 bg-gold/10 px-3 py-2 flex items-center gap-2" data-testid="blueprint-entitlement">
          {ent.free ? <Sparkles size={13} className="text-gold" /> : <Lock size={13} className="text-gold" />}
          <p className="font-mono text-[9px] text-white/70 leading-tight flex-1">
            {ent.free ? "Publishing is included in your plan." : `Publishing costs ${ent.publish_cost} Credits.`}
          </p>
          <span className="font-mono text-[9px] text-gold font-black">{ent.balance} Cr</span>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 py-3 space-y-3">
        {/* Saved designs */}
        {designs.length > 0 && (
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="blueprint-saved-list">
            {designs.map((d) => (
              <button key={d.id} onClick={() => loadDesign(d.id)} data-testid={`blueprint-open-${d.id}`}
                className={`shrink-0 flex items-center gap-1.5 rounded-xl border-2 px-3 py-2 font-mono text-[9px] font-black uppercase tracking-wide ${current?.id === d.id ? "border-gold bg-gold text-black" : "border-white/15 bg-white/5 text-white/60"}`}>
                <FileJson size={12} /> {d.title?.slice(0, 16) || "Untitled"}{d.published ? " ✓" : ""}
              </button>
            ))}
          </div>
        )}

        {/* Title */}
        {/* Optional nickname — auto-named when left blank (No-Typing rule) */}
        <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="blueprint-title"
          className="w-full bg-white/5 border-2 border-white/15 rounded-xl px-3 py-2.5 font-serif text-lg text-white outline-none focus:ring-2 focus:ring-gold" placeholder={`${autoTitle} — tap to add a nickname (optional)`} />

        {/* Matrix canvas */}
        <div className="rounded-2xl border-2 border-dashed border-white/15 bg-white/[0.02] p-2.5 space-y-2.5 min-h-[120px]" data-testid="blueprint-canvas">
          {matrix.length === 0 ? (
            <p className="text-center font-mono text-[10px] text-white/70 py-8">Empty canvas — add a row below.</p>
          ) : matrix.map((row, ri) => (
            <div key={ri} className="rounded-xl border-2 border-white/10 bg-slate-900 p-2" data-testid={`blueprint-row-${ri}`}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="font-mono text-[8px] uppercase tracking-widest text-white/70">Row {ri + 1} · {row.cols.length} col</span>
                <div className="flex gap-1">
                  <button onClick={() => moveRow(ri, -1)} data-testid={`blueprint-row-up-${ri}`} className="w-6 h-6 grid place-items-center rounded bg-white/5 border border-white/15"><ChevronUp size={11} className="text-white/60" /></button>
                  <button onClick={() => moveRow(ri, 1)} data-testid={`blueprint-row-down-${ri}`} className="w-6 h-6 grid place-items-center rounded bg-white/5 border border-white/15"><ChevronDown size={11} className="text-white/60" /></button>
                  <button onClick={() => delRow(ri)} data-testid={`blueprint-row-del-${ri}`} className="w-6 h-6 grid place-items-center rounded bg-red-600/80 border border-white/15"><Trash2 size={11} className="text-white" /></button>
                </div>
              </div>
              <div className={`grid gap-2 ${row.cols.length === 2 ? "grid-cols-2" : "grid-cols-1"}`}>
                {row.cols.map((cell, ci) => (
                  <div key={ci} className="rounded-lg border-2 p-2 bg-black/30" style={{ borderColor: `${cell.color}88` }} data-testid={`blueprint-cell-${ri}-${ci}`}>
                    <select value={cell.type} onChange={(e) => setCell(ri, ci, { type: e.target.value, color: typeMeta(e.target.value).color })}
                      data-testid={`blueprint-cell-type-${ri}-${ci}`} className="w-full mb-1.5 bg-slate-800 border border-white/15 rounded px-1.5 py-1 font-mono text-[9px] text-white outline-none">
                      {BLOCK_TYPES.map((b) => <option key={b.t} value={b.t}>{b.label}</option>)}
                    </select>
                    <input value={cell.label} onChange={(e) => setCell(ri, ci, { label: e.target.value })} data-testid={`blueprint-cell-label-${ri}-${ci}`}
                      className="w-full bg-white/5 border border-white/10 rounded px-1.5 py-1 font-mono text-[10px] text-white outline-none focus:ring-1 focus:ring-gold" placeholder="Label…" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Add-row toolbar */}
        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => addRow(1)} data-testid="blueprint-add-row-1" className="flex items-center justify-center gap-1.5 rounded-xl border-2 border-white/15 bg-white/5 py-2.5 font-mono text-[9px] font-black uppercase tracking-widest text-white/70"><Square size={12} /> 1-Col Row</button>
          <button onClick={() => addRow(2)} data-testid="blueprint-add-row-2" className="flex items-center justify-center gap-1.5 rounded-xl border-2 border-white/15 bg-white/5 py-2.5 font-mono text-[9px] font-black uppercase tracking-widest text-white/70"><Columns2 size={12} /> 2-Col Row</button>
        </div>
      </div>

      {/* Sticky action bar */}
      <div className="shrink-0 border-t border-white/10 p-3 pb-6 space-y-2">
        {current?.published && (
          <button onClick={renderSheet} disabled={rendering} data-testid="blueprint-render" className="w-full flex items-center justify-center gap-1.5 rounded-full border-2 border-gold bg-gold/10 py-2.5 font-mono text-[10px] font-black uppercase tracking-widest text-gold active:scale-[0.98] disabled:opacity-60">
            {rendering ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />} Render Layout Sheet (PDF)
          </button>
        )}
        <div className="grid grid-cols-2 gap-2">
          <button onClick={save} disabled={busy} data-testid="blueprint-save" className="flex items-center justify-center gap-1.5 rounded-full border-2 border-white bg-white text-slate-950 py-3 font-mono text-[10px] font-black uppercase tracking-widest active:scale-[0.98] disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Draft
          </button>
          <button onClick={publish} disabled={busy} data-testid="blueprint-publish" className="flex items-center justify-center gap-1.5 rounded-full border-2 border-charcoal bg-gold text-black py-3 font-mono text-[10px] font-black uppercase tracking-widest shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />} Publish
          </button>
        </div>
      </div>

      {/* Pay-to-play paywall */}
      <CreditPaywall detail={paywall} onClose={() => setPaywall(null)} />
    </div>
  );
}
